import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Hero, type HeroIconTint } from "./Hero";

/**
 * Canonical page shell for the Lilach dashboard.
 *
 * Gives every content page a consistent frame:
 *   - tight editorial reading column (max-w-3xl)
 *   - warm amber-tinted page background
 *   - <Hero> header (icon badge + title + subtitle, plus optional eyebrow,
 *     meta, and actions)
 *   - comfortable top/bottom padding
 *
 * Every section inside should be a <SectionCard> (or a <Callout>).
 *
 * The original PageShell API (icon / iconColor / title / subtitle /
 * actions / children / className) is preserved unchanged. New optional
 * props are forwarded to <Hero>.
 */
export type IconTint = HeroIconTint;

export type PageShellProps = {
  /** Optional Lucide icon displayed in a colored rounded badge. */
  icon?: LucideIcon;
  /** Tint used for the icon badge. Default: amber. */
  iconColor?: IconTint;
  /** Optional small uppercase kicker above the title. */
  eyebrow?: string;
  /** Large h1 title. Hebrew-ready. */
  title: string;
  /** Muted one-line subtitle / description. */
  subtitle?: string;
  /** Optional metadata row (timestamps, tags, status pills). */
  meta?: ReactNode;
  /** Optional actions rendered at the inline-end of the hero. */
  actions?: ReactNode;
  /** Wrap the hero in a soft amber-gradient backdrop card. Default: false. */
  heroBackdrop?: boolean;
  /** Page body — typically a stack of <SectionCard> children. */
  children: ReactNode;
  /** Reading column max width. Default: "max-w-3xl" for editorial guides. */
  maxWidth?: "max-w-3xl" | "max-w-4xl" | "max-w-5xl";
  /** Extra classes on the outer wrapper. */
  className?: string;
};

export function PageShell({
  icon,
  iconColor = "amber",
  eyebrow,
  title,
  subtitle,
  meta,
  actions,
  heroBackdrop = false,
  children,
  maxWidth = "max-w-3xl",
  className,
}: PageShellProps) {
  return (
    <div
      className={cn(
        // Studio canvas — bg-stone-50 matches the Layout's <main> tint
        // so content reads as floating on a single editorial canvas.
        // `min-h-svh` + negative margins escape the Layout's content-
        // sized `<div>` wrapper so the tint covers the entire scrollable
        // area. `h-full` + the parent flex makes tall content scroll
        // naturally inside <main>.
        "min-h-svh h-full bg-stone-50",
        "-mx-6 -my-8 px-6 py-6 sm:py-8",
        className,
      )}
    >
      <div className={cn("mx-auto", maxWidth)}>
        <div className="mb-6">
          <Hero
            icon={icon}
            iconColor={iconColor}
            eyebrow={eyebrow}
            title={title}
            subtitle={subtitle}
            meta={meta}
            actions={actions}
            withBackdrop={heroBackdrop}
          />
        </div>

        <div className="space-y-12">{children}</div>
      </div>
    </div>
  );
}
